import React from "react";
import * as Icon from "react-feather";

/**
 * NMU-488 / NMU-489: the icon keys an editor can pick for an intranet card,
 * paired with the glyph the admin draws for each.
 *
 * The keys are the contract with the portal — nmu-web's `lib/intranet-icons.tsx`
 * maps the same strings to its own (lucide) set, so the two stay in step by
 * sharing the key, not the library. Adding a key here without adding it there
 * makes the portal fall back to a document icon, so change both together.
 *
 * react-feather has no graduation cap or headset; `Award` and `Headphones` are
 * the closest members of the set the rest of the admin already uses, and the
 * label still says what the card is for.
 */
export const INTRANET_ICONS = [
  { value: "calendar", label: "ปฏิทิน (calendar)", icon: Icon.Calendar },
  { value: "folder", label: "โฟลเดอร์ (folder)", icon: Icon.Folder },
  { value: "layers", label: "ระบบงาน (layers)", icon: Icon.Layers },
  { value: "heart-handshake", label: "สวัสดิการ (heart)", icon: Icon.Heart },
  { value: "clock", label: "เวลาทำงาน (clock)", icon: Icon.Clock },
  { value: "graduation-cap", label: "พัฒนาบุคลากร (graduation)", icon: Icon.Award },
  { value: "file-text", label: "เอกสาร (file)", icon: Icon.FileText },
  { value: "users", label: "ทำเนียบบุคลากร (users)", icon: Icon.Users },
  { value: "bookmark", label: "จองทรัพยากร (bookmark)", icon: Icon.Bookmark },
  { value: "headset", label: "Helpdesk (headset)", icon: Icon.Headphones },
];

/** Aliases the portal also accepts, so a record saved elsewhere still draws. */
const ALIASES = {
  heart: "heart-handshake",
  graduation: "graduation-cap",
  file: "file-text",
};

const BY_VALUE = INTRANET_ICONS.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

/** The theme's primary dark green — the colour the portal draws these in. */
export const INTRANET_ICON_COLOR = "#0D2F23";

/**
 * The component for an icon key. A missing or unknown key falls back to the
 * document icon rather than rendering nothing, so a row keeps its column width.
 */
export function intranetIconComponent(value) {
  if (!value) return Icon.FileText;
  const key = String(value).trim().toLowerCase();
  const opt = BY_VALUE[ALIASES[key] || key];
  return opt ? opt.icon : Icon.FileText;
}

/**
 * NMU-489: the icon as the backoffice table renders it — fixed 24px, primary
 * dark green, centred in its cell.
 */
export function IntranetIcon({ value, size = 24 }) {
  const Glyph = intranetIconComponent(value);
  return (
    <div className="d-flex justify-content-center">
      <Glyph size={size} color={INTRANET_ICON_COLOR} />
    </div>
  );
}
